import React, { useState, useEffect, useRef } from "react";
import {
  Row,
  Col,
  Table,
  Tag,
  Drawer,
  Descriptions,
  Divider,
  Progress,
  Spin,
  Collapse,
  Statistic,
  Tooltip,
} from "antd";
import { motion } from "framer-motion";
import { Card } from "reactstrap";
import Head from "../../layout/head/Head";
import Content from "../../layout/content/Content";
import {
  BlockHead,
  BlockHeadContent,
  BlockTitle,
} from "../../components/block/Block";
import { formatCurrency } from "../../utils/Utils";
import { useNavigate } from "react-router-dom";
import {
  WarningOutlined,
  BellOutlined,
  RobotOutlined,
  LoadingOutlined,
  CheckCircleOutlined,
  DollarOutlined,
  ArrowRightOutlined,
  ExclamationCircleOutlined,
  ClockCircleOutlined,
  CaretRightOutlined,
  EnvironmentOutlined,
} from "@ant-design/icons";
import { ShimmerTableCard } from "../procurement-inventory-control-tower/components/ProcurementShimmerLoader";
import ReactECharts from "echarts-for-react";

const { Panel } = Collapse;

interface RiskCascade {
  key: string;
  riskNumber: string;
  sourceRisk: string;
  originModule: string;
  cascadeImpactArea: string;
  detectionLogic: string;
  typeOfImpact: string;
  monetaryImpact: number;
  exampleImpact: string;
  responsiblePerson: string;
  severity: "High" | "Medium" | "Low";
}

interface ModuleRisk {
  key: string;
  riskType: string;
  module: string;
  count: number;
  monetaryImpact: number;
  severity: "High" | "Medium" | "Low";
  status: "Active" | "Mitigated" | "Pending";
}

const IntegratedRiskControlTower: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [cascadeDrawerVisible, setCascadeDrawerVisible] = useState(false);
  const [selectedCascade, setSelectedCascade] = useState<RiskCascade | null>(
    null
  );
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);

  // Module to route mapping
  const getModuleRoute = (module: string): string | null => {
    const moduleRouteMap: { [key: string]: string } = {
      "Project Management": "/project-risk-dashboard",
      Procurement: "/procurement-inventory-control-tower",
      Inventory: "/procurement-inventory-control-tower",
      "Work Orders": "/work-order-execution-payment-control-tower",
      Finance: "/work-order-execution-payment-control-tower",
      "Budget Control": "/budget-control-tower",
    };
    return moduleRouteMap[module] || null;
  };

  useEffect(() => {
    setTimeout(() => setLoading(false), 1500);
  }, []);

  // Initialize Leaflet map
  useEffect(() => {
    if (!loading && mapRef.current && !mapInstanceRef.current) {
      // Load Leaflet CSS and JS dynamically
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
      document.head.appendChild(link);

      const script = document.createElement("script");
      script.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
      script.onload = () => {
        const L = (window as any).L;

        // Initialize map centered on Hyderabad
        const map = L.map(mapRef.current).setView([17.385, 78.4], 11);

        // Add OpenStreetMap tiles
        L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
          attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
          maxZoom: 18,
        }).addTo(map);

        // Custom marker icons based on risk level
        const createCustomIcon = (color: string, status: string) => {
          return L.divIcon({
            className: "custom-marker",
            html: `
              <div style="
                position: relative;
                width: 40px;
                height: 40px;
                display: flex;
                align-items: center;
                justify-content: center;
              ">
                <div style="
                  width: 36px;
                  height: 36px;
                  background: ${color};
                  border-radius: 50%;
                  border: 3px solid white;
                  box-shadow: 0 2px 8px rgba(0,0,0,0.3);
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  font-size: 18px;
                ">
                  ${status}
                </div>
                <div style="
                  position: absolute;
                  bottom: -8px;
                  left: 50%;
                  transform: translateX(-50%);
                  width: 0;
                  height: 0;
                  border-left: 8px solid transparent;
                  border-right: 8px solid transparent;
                  border-top: 10px solid ${color};
                  filter: drop-shadow(0 2px 2px rgba(0,0,0,0.2));
                "></div>
              </div>
            `,
            iconSize: [40, 50],
            iconAnchor: [20, 50],
            popupAnchor: [0, -50],
          });
        };

        // Add markers for each project
        projectLocations.forEach((project) => {
          const marker = L.marker([project.coordinates[0], project.coordinates[1]], {
            icon: createCustomIcon(project.riskColor, project.status),
          }).addTo(map);

          // Create popup content
          const popupContent = `
            <div style="min-width: 220px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">
              <div style="font-size: 14px; font-weight: 600; color: #262626; margin-bottom: 6px;">
                ${project.name}
              </div>
              <div style="font-size: 11px; color: #8c8c8c; margin-bottom: 10px; display: flex; align-items: center; gap: 4px;">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="#8c8c8c">
                  <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                </svg>
                ${project.location}
              </div>
              <div style="padding: 8px; background: #fafafa; border-radius: 4px; margin-bottom: 8px;">
                <div style="font-size: 11px; color: #595959; margin-bottom: 4px;">
                  <strong style="color: ${project.riskColor};">Risk Level:</strong> ${project.riskLevel}
                </div>
                <div style="font-size: 11px; color: #595959; margin-bottom: 4px;">
                  <strong>Risk Value:</strong> ${formatCurrency(project.riskValue, "INR")}
                </div>
                <div style="font-size: 11px; color: #595959;">
                  <strong>Delayed Tasks:</strong> ${project.delayedTasks}
                </div>
              </div>
              <div style="font-size: 10px; color: #8c8c8c; font-style: italic;">
                Click marker for details
              </div>
            </div>
          `;

          marker.bindPopup(popupContent, {
            maxWidth: 280,
            className: "custom-popup",
          });

          // Open popup on hover
          marker.on("mouseover", () => {
            marker.openPopup();
          });
        });

        mapInstanceRef.current = map;
      };
      document.head.appendChild(script);
    }

    // Cleanup
    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [loading]);

  // Project location data with coordinates (Hyderabad area)
  const projectLocations = [
    {
      name: "Grandeur Heights",
      location: "Kokapet, Opp Exit 18 A, Hyderabad",
      coordinates: [17.3850, 78.3510],
      riskLevel: "High",
      riskColor: "#ff4d4f",
      riskValue: 3800000,
      delayedTasks: 12,
      status: "🔴",
    },
    {
      name: "Skyline Royale",
      location: "Kollur, Hyderabad, Telangana",
      coordinates: [17.4270, 78.3835],
      riskLevel: "High",
      riskColor: "#ff4d4f",
      riskValue: 3000000,
      delayedTasks: 10,
      status: "🔴",
    },
    {
      name: "Elysium Towers",
      location: "Kollur, Hyderabad, Telangana",
      coordinates: [17.4300, 78.3900],
      riskLevel: "Medium",
      riskColor: "#faad14",
      riskValue: 2200000,
      delayedTasks: 8,
      status: "🟠",
    },
    {
      name: "Opéra Sky",
      location: "Kollur, Hyderabad, Telangana",
      coordinates: [17.4240, 78.3770],
      riskLevel: "Low",
      riskColor: "#52c41a",
      riskValue: 800000,
      delayedTasks: 3,
      status: "🟢",
    },
    {
      name: "Paramount Rise",
      location: "Kongara Kalan, Adibatla, Hyderabad",
      coordinates: [17.3180, 78.5470],
      riskLevel: "Medium",
      riskColor: "#faad14",
      riskValue: 1700000,
      delayedTasks: 6,
      status: "🟠",
    },
  ];

  // Module-wise Risk Data (matching Cross-Module Risk Flow breakdown)
  const moduleRisks: ModuleRisk[] = [
    {
      key: "1",
      riskType: "Delayed Tasks",
      module: "Project Management",
      count: 42,
      monetaryImpact: 11500000, // ₹1.15 Cr
      severity: "High",
      status: "Active",
    },
    {
      key: "2",
      riskType: "Delayed Deliveries",
      module: "Procurement",
      count: 18,
      monetaryImpact: 1500000, // ₹15L
      severity: "High",
      status: "Active",
    },
    {
      key: "3",
      riskType: "Supplier Delivery Delays",
      module: "Procurement",
      count: 8,
      monetaryImpact: 759574, // ₹7.60L
      severity: "High",
      status: "Active",
    },
    {
      key: "4",
      riskType: "Stock-Out Risk",
      module: "Inventory",
      count: 7,
      monetaryImpact: 550000, // ₹5.50L
      severity: "High",
      status: "Active",
    },
    {
      key: "5",
      riskType: "Delayed Work Execution",
      module: "Work Orders",
      count: 2,
      monetaryImpact: 280000, // ₹2.80L
      severity: "High",
      status: "Active",
    },
    {
      key: "6",
      riskType: "RA Bill Approval Delays",
      module: "Work Orders",
      count: 2,
      monetaryImpact: 125000, // ₹1.25L
      severity: "High",
      status: "Active",
    },
    {
      key: "7",
      riskType: "Payment Delays",
      module: "Work Orders",
      count: 2,
      monetaryImpact: 88295, // ₹0.88L
      severity: "Medium",
      status: "Active",
    },
    {
      key: "8",
      riskType: "Budget Overrun",
      module: "Budget Control",
      count: 1,
      monetaryImpact: 8750000, // ₹87.50L
      severity: "High",
      status: "Active",
    },
  ];

  // Executive Summary Metrics (calculated from moduleRisks)
  const totalActiveRisks = moduleRisks.length; // Total count of all module risks

  // Calculate monetary impacts by severity
  const highImpactRisks = moduleRisks
    .filter((r) => r.severity === "High")
    .reduce((sum, r) => sum + r.monetaryImpact, 0);

  const mediumImpactRisks = moduleRisks
    .filter((r) => r.severity === "Medium")
    .reduce((sum, r) => sum + r.monetaryImpact, 0);

  const lowImpactRisks = moduleRisks
    .filter((r) => r.severity === "Low")
    .reduce((sum, r) => sum + r.monetaryImpact, 0);

  const totalMonetaryImpact = moduleRisks.reduce(
    (sum, r) => sum + r.monetaryImpact,
    0
  );

  // Budget variance metrics
  const budgetVariance = 3.6; // Budget variance (%)
  const budgetVarianceValue = 8750000; // ₹87.50L
  const budgetVarianceTrend = "up"; // Indicates increase

  // Overall Risk Index (calculated from severity and count)
  const overallRiskIndex = 7.5; // Risk index on scale of 0-10

  // Risk Cascade Data (aligned with Cross-Module Risk Flow)
  const riskCascades: RiskCascade[] = [
    {
      key: "1",
      riskNumber: "#1",
      sourceRisk: "Task Delay in Project Management",
      originModule: "Project Management",
      cascadeImpactArea: "Procurement Delay",
      detectionLogic:
        "Actual Progress < Planned - 15% and task has material BOQ → PR not raised",
      typeOfImpact: "Material Delay & Idle Resource Cost",
      monetaryImpact: 11500000,
      exampleImpact: "₹1.15 Cr cascaded to procurement",
      responsiblePerson: "Rajesh Kumar (PM)",
      severity: "High",
    },
    {
      key: "2",
      riskNumber: "#2",
      sourceRisk: "Procurement Delay to Inventory",
      originModule: "Procurement",
      cascadeImpactArea: "Inventory Stock-Out Risk",
      detectionLogic: "Material delivery delay causes inventory shortage",
      typeOfImpact: "Stock-Out & Emergency Procurement",
      monetaryImpact: 1500000,
      exampleImpact: "Delayed Deliveries = ₹15.00L",
      responsiblePerson: "Priya Sharma (Procurement)",
      severity: "High",
    },
    {
      key: "3",
      riskNumber: "#3",
      sourceRisk: "Procurement Delay to Work Orders",
      originModule: "Procurement",
      cascadeImpactArea: "Work Execution Delay",
      detectionLogic: "PR approved < 10 days before planned start",
      typeOfImpact: "Execution Lag & Idle Contractor Cost",
      monetaryImpact: 759574,
      exampleImpact: "Supplier Delivery Delays = ₹7.60L",
      responsiblePerson: "Priya Sharma (Procurement)",
      severity: "High",
    },
    {
      key: "4",
      riskNumber: "#4",
      sourceRisk: "Material Shortage at Site",
      originModule: "Inventory",
      cascadeImpactArea: "Work Order Execution",
      detectionLogic: "OnHand < SafetyStock & Task Start ≤ 7 days",
      typeOfImpact: "Idle Contractor + Re-procurement at higher cost",
      monetaryImpact: 550000,
      exampleImpact: "Stock-Out Risk = ₹5.50L",
      responsiblePerson: "Suresh Reddy (Stores)",
      severity: "High",
    },
    {
      key: "5",
      riskNumber: "#5",
      sourceRisk: "Work Execution Delay",
      originModule: "Work Orders",
      cascadeImpactArea: "Payment Delay & Cash Flow Impact",
      detectionLogic:
        "Actual Work % < Planned % by 15% → RA Bill delay → Payment delay",
      typeOfImpact: "Delayed Revenue & Cash Flow Impact",
      monetaryImpact: 280000,
      exampleImpact: "Delayed Work Execution = ₹2.80L",
      responsiblePerson: "Vinay Patel (Contractor)",
      severity: "High",
    },
    {
      key: "6",
      riskNumber: "#6",
      sourceRisk: "RA Bill Approval Delay",
      originModule: "Work Orders",
      cascadeImpactArea: "Budget Impact & Work Slowdown",
      detectionLogic:
        "RA Bill pending approval > 7 days → Budget reallocation needed",
      typeOfImpact: "Budget Pressure & Project Delays",
      monetaryImpact: 125000,
      exampleImpact: "RA Bill Approval Delays = ₹1.25L",
      responsiblePerson: "Amit Verma (Finance)",
      severity: "High",
    },
    {
      key: "7",
      riskNumber: "#7",
      sourceRisk: "Payment Delay Feedback Loop",
      originModule: "Work Orders",
      cascadeImpactArea: "Work Execution & Labour Motivation",
      detectionLogic: "Approved RA Bill unpaid > 15 days → Contractor slowdown",
      typeOfImpact: "Slowdown in Execution",
      monetaryImpact: 88295,
      exampleImpact: "Payment Delays = ₹0.88L",
      responsiblePerson: "Meena Iyer (Finance)",
      severity: "Medium",
    },
    {
      key: "8",
      riskNumber: "#8",
      sourceRisk: "Budget Overrun",
      originModule: "Budget Control",
      cascadeImpactArea: "Project Financial Health",
      detectionLogic: "Accrued > Budget OR Forecast > Budget",
      typeOfImpact: "Budget Breach & Cost Overrun",
      monetaryImpact: 8750000,
      exampleImpact: "Budget Overrun = ₹87.50L",
      responsiblePerson: "CFO / Project Director",
      severity: "High",
    },
  ];

  const moduleRiskColumns = [
    {
      title: "Risk Type",
      dataIndex: "riskType",
      key: "riskType",
      width: 200,
      render: (text: string) => <span style={{ fontWeight: 600 }}>{text}</span>,
    },
    {
      title: "Module",
      dataIndex: "module",
      key: "module",
      width: 150,
      render: (text: string, record: any) => {
        const route = getModuleRoute(text);
        return (
          <Tag
            color="blue"
            style={{
              fontSize: 11,
              cursor: route ? "pointer" : "default",
              textDecoration: route ? "underline" : "none",
            }}
            onClick={() => {
              if (route) navigate(route);
            }}
          >
            {text}
          </Tag>
        );
      },
    },
    {
      title: "Count",
      dataIndex: "count",
      key: "count",
      width: 80,
      align: "center" as const,
      render: (val: number, record: any) => {
        const route = getModuleRoute(record.module);
        return (
          <Tag
            color={val >= 5 ? "red" : val >= 3 ? "orange" : "green"}
            style={{
              cursor: route ? "pointer" : "default",
              textDecoration: route ? "underline" : "none",
            }}
            onClick={() => {
              if (route) navigate(route);
            }}
          >
            {val}
          </Tag>
        );
      },
    },
    {
      title: "Monetary Impact (₹)",
      dataIndex: "monetaryImpact",
      key: "monetaryImpact",
      width: 150,
      align: "right" as const,
      render: (val: number) => (
        <span
          style={{
            fontWeight: 600,
            color: val > 500000 ? "#ff4d4f" : "#faad14",
          }}
        >
          {formatCurrency(val, "INR")}
        </span>
      ),
    },
    {
      title: "Severity",
      dataIndex: "severity",
      key: "severity",
      width: 100,
      align: "center" as const,
      render: (val: string) => (
        <Tag
          color={val === "High" ? "red" : val === "Medium" ? "orange" : "green"}
        >
          {val}
        </Tag>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 100,
      render: (status: string) => (
        <Tag
          color={
            status === "Active"
              ? "red"
              : status === "Mitigated"
              ? "green"
              : "orange"
          }
        >
          {status}
        </Tag>
      ),
    },
  ];

  const cascadeColumns = [
    // {
    //   title: "#",
    //   dataIndex: "riskNumber",
    //   key: "riskNumber",
    //   width: 60,
    //   render: (text: string) => <span style={{ fontWeight: 600 }}>{text}</span>,
    // },
    {
      title: "Source Risk",
      dataIndex: "sourceRisk",
      key: "sourceRisk",
      width: 200,
      // render: (text: string) => <span style={{ fontWeight: 600, textDecoration: "underline", cursor: "pointer" }}>{text}</span>,
    },
    {
      title: "Origin Module",
      dataIndex: "originModule",
      key: "originModule",
      width: 150,
      render: (text: string) => (
        <Tag color="blue" style={{ fontSize: 11 }}>
          {text}
        </Tag>
      ),
    },
    {
      title: "Cascade Impact",
      dataIndex: "cascadeImpactArea",
      key: "cascadeImpactArea",
      width: 180,
      render: (text: string, record: RiskCascade) => (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 11 }}>{record.originModule}</span>
          <ArrowRightOutlined style={{ fontSize: 10, color: "#ff4d4f" }} />
          <span style={{ fontSize: 11, fontWeight: 600 }}>{text}</span>
        </div>
      ),
    },
    {
      title: "Type of Impact",
      dataIndex: "typeOfImpact",
      key: "typeOfImpact",
      width: 180,
    },
    {
      title: "Monetary Impact (₹)",
      dataIndex: "monetaryImpact",
      key: "monetaryImpact",
      width: 150,
      align: "right" as const,
      render: (val: number) => (
        <span style={{ fontWeight: 600, color: "#ff4d4f" }}>
          {formatCurrency(val, "INR")}
        </span>
      ),
    },
    {
      title: "Responsible",
      dataIndex: "responsiblePerson",
      key: "responsiblePerson",
      width: 150,
    },
    {
      title: "Severity",
      dataIndex: "severity",
      key: "severity",
      width: 100,
      render: (val: string) => (
        <Tag
          color={val === "High" ? "red" : val === "Medium" ? "orange" : "green"}
        >
          {val}
        </Tag>
      ),
    },
  ];

  return (
    <>
      <Head title="Integrated Risk Impact Control Tower" />
      <Content>
        <BlockHead size="sm">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4 }}
          >
            <BlockHeadContent>
              <BlockTitle tag="h3" page>
                Integrated Risk Impact Control Tower Dashboard
              </BlockTitle>
              <p style={{ color: "#8c8c8c", marginTop: 8 }}>
                Unified real-time view of operational, financial, and execution
                risks across Project Management, Procurement, Work Orders, and
                Finance
              </p>
            </BlockHeadContent>
          </motion.div>
        </BlockHead>

        <Card className="card-bordered" style={{ overflow: "hidden" }}>
          <div className="card-aside-wrap" style={{ overflow: "hidden" }}>
            <div
              className="card-inner card-inner-lg"
              style={{ overflowX: "auto" }}
            >
              {/* Executive Risk Summary */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                style={{ marginBottom: 32 }}
              >
                <h5 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>
                  Executive Risk Summary
                </h5>
                <div style={{ marginLeft: -8, marginRight: -8 }}>
                  {loading ? (
                    <Row gutter={[16, 16]}>
                      {[1, 2, 3, 4, 5, 6].map((i) => (
                        <Col key={i} xs={24} sm={12} lg={8}>
                          <ShimmerTableCard rows={1} />
                        </Col>
                      ))}
                    </Row>
                  ) : (
                    <Table
                      dataSource={[
                        {
                          key: "1",
                          metric1: "Total Active Risks",
                          value1: totalActiveRisks,
                          status1:
                            totalActiveRisks > 10
                              ? "🔴 High"
                              : totalActiveRisks > 5
                              ? "🟡 Medium"
                              : "🟢 Low",
                          color1:
                            totalActiveRisks > 10
                              ? "#cf1322"
                              : totalActiveRisks > 5
                              ? "#fa8c16"
                              : "#52c41a",
                          bg1:
                            totalActiveRisks > 10
                              ? "#fff1f0"
                              : totalActiveRisks > 5
                              ? "#fff7e6"
                              : "#f6ffed",
                          metric2: "Budget Variance",
                          value2: `${formatCurrency(
                            budgetVarianceValue,
                            "INR"
                          )} (${budgetVariance.toFixed(1)}%)`,
                          status2:
                            budgetVarianceTrend === "up"
                              ? "🔴 ▲ Increasing"
                              : "🟢 ▼ Decreasing",
                          color2:
                            budgetVarianceTrend === "up"
                              ? "#cf1322"
                              : "#52c41a",
                          bg2:
                            budgetVarianceTrend === "up"
                              ? "#fff1f0"
                              : "#f6ffed",
                          metric3: "Total Monetary Impact",
                          value3: formatCurrency(totalMonetaryImpact, "INR"),
                          status3: "🔴 Critical",
                          color3: "#cf1322",
                          bg3: "#fff1f0",
                        },
                        {
                          key: "2",
                          metric1: "High Impact Risks (₹)",
                          value1: formatCurrency(highImpactRisks, "INR"),
                          status1: "🔴 Critical",
                          color1: "#cf1322",
                          bg1: "#fff1f0",
                          metric2: "Medium Impact Risks (₹)",
                          value2: formatCurrency(mediumImpactRisks, "INR"),
                          status2: "🟡 Moderate",
                          color2: "#fa8c16",
                          bg2: "#fff7e6",
                          metric3: "Low Impact Risks (₹)",
                          value3:
                            lowImpactRisks > 0
                              ? formatCurrency(lowImpactRisks, "INR")
                              : "₹0",
                          status3: "🟢 Minor",
                          color3: "#52c41a",
                          bg3: "#f6ffed",
                        },
                      ]}
                      columns={[
                        {
                          title: "Risk Indicator",
                          dataIndex: "metric1",
                          key: "metric1",
                          width: "18%",
                          render: (text: string, record: any) => (
                            <div
                              style={{
                                padding: "4px 8px",
                                background: record.bg1,
                                borderRadius: 4,
                                borderLeft: `3px solid ${record.color1}`,
                              }}
                            >
                              <span
                                style={{
                                  fontSize: 11,
                                  fontWeight: 600,
                                  color: "#262626",
                                }}
                              >
                                {text}
                              </span>
                            </div>
                          ),
                        },
                        {
                          title: "Value",
                          dataIndex: "value1",
                          key: "value1",
                          width: "8%",
                          align: "center" as const,
                          render: (text: any, record: any) => (
                            <span
                              style={{
                                fontSize: 18,
                                fontWeight: 700,
                                color: record.color1,
                              }}
                            >
                              {text}
                            </span>
                          ),
                        },
                        {
                          title: "",
                          dataIndex: "status1",
                          key: "status1",
                          width: "7%",
                          render: (text: string) => (
                            <span
                              style={{ fontSize: 10, whiteSpace: "nowrap" }}
                            >
                              {text}
                            </span>
                          ),
                        },
                        {
                          title: "Risk Indicator",
                          dataIndex: "metric2",
                          key: "metric2",
                          width: "10%",
                          render: (text: string, record: any) => (
                            <div
                              style={{
                                padding: "4px 8px",
                                background: record.bg2,
                                borderRadius: 4,
                                borderLeft: `3px solid ${record.color2}`,
                              }}
                            >
                              <span
                                style={{
                                  fontSize: 11,
                                  fontWeight: 600,
                                  color: "#262626",
                                }}
                              >
                                {text}
                              </span>
                            </div>
                          ),
                        },
                        {
                          title: "Value",
                          dataIndex: "value2",
                          key: "value2",
                          width: "10%",
                          align: "center" as const,
                          render: (text: any, record: any) => (
                            <span
                              style={{
                                fontSize: 15,
                                fontWeight: 700,
                                color: record.color2,
                              }}
                            >
                              {text}
                            </span>
                          ),
                        },
                        {
                          title: "",
                          dataIndex: "status2",
                          key: "status2",
                          width: "7%",
                          render: (text: string) => (
                            <span
                              style={{ fontSize: 10, whiteSpace: "nowrap" }}
                            >
                              {text}
                            </span>
                          ),
                        },
                        {
                          title: "Risk Indicator",
                          dataIndex: "metric3",
                          key: "metric3",
                          width: "18%",
                          render: (text: string, record: any) => (
                            <div
                              style={{
                                padding: "4px 8px",
                                background: record.bg3,
                                borderRadius: 4,
                                borderLeft: `3px solid ${record.color3}`,
                              }}
                            >
                              <span
                                style={{
                                  fontSize: 11,
                                  fontWeight: 600,
                                  color: "#262626",
                                }}
                              >
                                {text}
                              </span>
                            </div>
                          ),
                        },
                        {
                          title: "Value",
                          dataIndex: "value3",
                          key: "value3",
                          width: "8%",
                          align: "center" as const,
                          render: (text: any, record: any) => (
                            <span
                              style={{
                                fontSize: 18,
                                fontWeight: 700,
                                color: record.color3,
                              }}
                            >
                              {text}
                            </span>
                          ),
                        },
                        {
                          title: "",
                          dataIndex: "status3",
                          key: "status3",
                          width: "7%",
                          render: (text: string) => (
                            <span
                              style={{ fontSize: 10, whiteSpace: "nowrap" }}
                            >
                              {text}
                            </span>
                          ),
                        },
                      ]}
                      pagination={false}
                      size="small"
                      bordered
                      rowClassName={() => "risk-summary-row"}
                    />
                  )}
                </div>
              </motion.div>

              <Divider />

              {/* Cross-Module Risk Flow Visualization */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                style={{ marginBottom: 32 }}
              >
                <h5 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>
                  Cross-Module Risk Flow & Cascade
                </h5>
                {loading ? (
                  <ShimmerTableCard rows={6} />
                ) : (
                  <div
                    style={{
                      background: "#fff",
                      padding: 0,
                      borderRadius: 8,
                      border: "1px solid #e8e8e8",
                      overflow: "hidden",
                    }}
                  >
                    {/* Vertical Cascade Diagram */}
                    <Table
                      dataSource={[
                        {
                          key: "1",
                          level: "Level 1",
                          module: "📊 Project Management",
                          ownRisks: 1,
                          ownImpact: 11500000,
                          ownImpactDisplay: "₹1.15 Cr",
                          cascadeReceived: "—",
                          cascadeGiven: "₹1.15 Cr → Procurement",
                          totalExposure: "₹1.15 Cr",
                          severity: "critical",
                          breakdown: [
                            {
                              riskType: "Delayed Tasks",
                              count: 42,
                              impact: 11500000,
                              severity: "High",
                            },
                          ],
                        },
                        {
                          key: "2",
                          level: "Level 2",
                          module: "🛒 Procurement & Inventory",
                          ownRisks: 3,
                          ownImpact: 2809574,
                          ownImpactDisplay: "₹28.10 L",
                          cascadeReceived: "₹1.15 Cr ← PM",
                          cascadeGiven: "₹28.10 L → Work Orders",
                          totalExposure: "₹1.43 Cr",
                          severity: "high",
                          breakdown: [
                            {
                              riskType: "Delayed Deliveries",
                              count: 18,
                              impact: 1500000,
                              severity: "High",
                            },
                            {
                              riskType: "Supplier Delivery Delays",
                              count: 8,
                              impact: 759574,
                              severity: "High",
                            },
                            {
                              riskType: "Stock-Out Risk",
                              count: 7,
                              impact: 550000,
                              severity: "High",
                            },
                          ],
                        },
                        {
                          key: "3",
                          level: "Level 3",
                          module: "🏗️ Work Order Execution & Payments",
                          ownRisks: 3,
                          ownImpact: 493295,
                          ownImpactDisplay: "₹4.93 L",
                          cascadeReceived:
                            "₹28.10 L ← Procurement, ₹1.15 Cr ← PM",
                          cascadeGiven: "₹4.93 L → Budget",
                          totalExposure: "₹1.48 Cr",
                          severity: "high",
                          breakdown: [
                            {
                              riskType: "Delayed Work Execution",
                              count: 2,
                              impact: 280000,
                              severity: "High",
                            },
                            {
                              riskType: "RA Bill Approval Delays",
                              count: 2,
                              impact: 125000,
                              severity: "High",
                            },
                            {
                              riskType: "Payment Delays",
                              count: 2,
                              impact: 88295,
                              severity: "Medium",
                            },
                          ],
                        },
                        {
                          key: "4",
                          level: "Level 4",
                          module: "📊 Overall Budget Impact",
                          ownRisks: 1,
                          ownImpact: 8750000,
                          ownImpactDisplay: "₹87.50 L",
                          cascadeReceived:
                            "₹4.93 L ← Work Orders, ₹1.43 Cr ← Upstream",
                          cascadeGiven: "—",
                          totalExposure: "₹2.36 Cr",
                          severity: "critical",
                          breakdown: [
                            {
                              riskType: "Budget Overrun",
                              count: 1,
                              impact: 8750000,
                              severity: "High",
                            },
                          ],
                        },
                      ]}
                      columns={[
                        // {
                        //   title: "Cascade Level",
                        //   dataIndex: "level",
                        //   key: "level",
                        //   width: 100,
                        //   render: (text: string) => (
                        //     <div style={{ fontWeight: 600, fontSize: 12 }}>
                        //       {text}
                        //     </div>
                        //   ),
                        // },
                        {
                          title: "Module",
                          dataIndex: "module",
                          key: "module",
                          width: 200,
                          render: (text: string, record: any) => (
                            <div
                            // style={{
                            //   padding: "8px 12px",
                            //   background:
                            //     record.severity === "high"
                            //       ? "#fff1f0"
                            //       : record.severity === "critical"
                            //       ? "#f9f0ff"
                            //       : record.severity === "warning"
                            //       ? "#fff7e6"
                            //       : "#e6f7ff",
                            //   border:
                            //     record.severity === "high"
                            //       ? "2px solid #ff4d4f"
                            //       : record.severity === "critical"
                            //       ? "2px solid #722ed1"
                            //       : record.severity === "warning"
                            //       ? "2px solid #faad14"
                            //       : "2px solid #1890ff",
                            //   borderRadius: 6,
                            //   fontWeight: 600,
                            //   fontSize: 13,
                            // }}
                            >
                              {text}
                            </div>
                          ),
                        },
                        {
                          title: "Risks",
                          dataIndex: "ownRisks",
                          key: "ownRisks",
                          width: 80,
                          align: "center" as const,
                          render: (val: number) => {
                            return <Tag color="blue">{val}</Tag>;
                          },
                        },
                        {
                          title: "Risk Impact",
                          dataIndex: "ownImpactDisplay",
                          key: "ownImpactDisplay",
                          width: 100,
                          align: "center" as const,
                          render: (text: string, record: any) => {
                            const getRoute = () => {
                              if (record.module.includes("Project Management"))
                                return "/project-risk-dashboard";
                              if (
                                record.module.includes("Procurement") ||
                                record.module.includes("Inventory")
                              )
                                return "/procurement-inventory-control-tower";
                              if (
                                record.module.includes("Work Order") ||
                                record.module.includes("Payment")
                              )
                                return "/work-order-execution-payment-control-tower";
                              if (record.module.includes("Budget"))
                                return "/budget-control-tower";
                              return null;
                            };
                            const route = getRoute();

                            return (
                              <div
                                style={{
                                  cursor: route ? "pointer" : "default",
                                  fontWeight: 600,
                                  color: "#ff4d4f",
                                  textDecoration: route ? "underline" : "none",
                                }}
                                onClick={() => {
                                  if (route) navigate(route);
                                }}
                              >
                                {text}
                              </div>
                            );
                          },
                        },
                        {
                          title: "⬇️ Cascade Received",
                          dataIndex: "cascadeReceived",
                          key: "cascadeReceived",
                          width: 180,
                          render: (text: string) => (
                            <div style={{ fontSize: 12, color: "#8c8c8c" }}>
                              {text}
                            </div>
                          ),
                        },
                        {
                          title: "⬇️ Cascade Given",
                          dataIndex: "cascadeGiven",
                          key: "cascadeGiven",
                          width: 240,
                          render: (text: string) => (
                            <div style={{ fontSize: 12, color: "#ff7875" }}>
                              {text}
                            </div>
                          ),
                        },
                        {
                          title: "Total Exposure",
                          dataIndex: "totalExposure",
                          key: "totalExposure",
                          width: 130,
                          align: "center" as const,
                          render: (text: string, record: any) => (
                            <div
                              style={{
                                fontWeight: 700,
                                fontSize: 13,
                                color:
                                  record.severity === "high" ||
                                  record.severity === "critical"
                                    ? "#ff4d4f"
                                    : record.severity === "warning"
                                    ? "#faad14"
                                    : "#52c41a",
                              }}
                            >
                              {text}
                            </div>
                          ),
                        },
                      ]}
                      pagination={false}
                      size="small"
                      bordered
                      expandable={{
                        expandedRowKeys: expandedRowKeys,
                        onExpand: (expanded, record) => {
                          if (expanded) {
                            setExpandedRowKeys([record.key]);
                          } else {
                            setExpandedRowKeys([]);
                          }
                        },
                        expandedRowRender: (record: any) => (
                          <div style={{ padding: 16, background: "#fafafa" }}>
                            <h6
                              style={{
                                fontSize: 13,
                                fontWeight: 600,
                                marginBottom: 12,
                                color: "#262626",
                              }}
                            >
                              Module-wise Risk Breakdown
                            </h6>
                            <Table
                              columns={[
                                {
                                  title: "Risk Type",
                                  dataIndex: "riskType",
                                  key: "riskType",
                                  width: 200,
                                },
                                {
                                  title: "Count",
                                  dataIndex: "count",
                                  key: "count",
                                  width: 80,
                                  align: "center" as const,
                                },
                                {
                                  title: "Impact (₹)",
                                  dataIndex: "impact",
                                  key: "impact",
                                  width: 120,
                                  align: "right" as const,
                                  render: (val: number) => (
                                    <span
                                      style={{
                                        fontWeight: 600,
                                        color: "#ff4d4f",
                                      }}
                                    >
                                      {new Intl.NumberFormat("en-IN", {
                                        style: "currency",
                                        currency: "INR",
                                        minimumFractionDigits: 0,
                                      }).format(val)}
                                    </span>
                                  ),
                                },
                                {
                                  title: "Severity",
                                  dataIndex: "severity",
                                  key: "severity",
                                  width: 100,
                                  align: "center" as const,
                                  render: (severity: string) => (
                                    <Tag
                                      color={
                                        severity === "High"
                                          ? "red"
                                          : severity === "Medium"
                                          ? "orange"
                                          : "green"
                                      }
                                    >
                                      {severity}
                                    </Tag>
                                  ),
                                },
                              ]}
                              dataSource={record.breakdown}
                              pagination={false}
                              size="small"
                              rowKey="riskType"
                            />
                          </div>
                        ),
                        rowExpandable: (record: any) =>
                          record.breakdown && record.breakdown.length > 0,
                      }}
                    />
                  </div>
                )}

                <div
                  style={{
                    marginTop: 16,
                    padding: "12px 16px",
                    background: "#e6f7ff",
                    borderRadius: 6,
                    borderLeft: "3px solid #1890ff",
                  }}
                >
                  <p
                    style={{
                      fontSize: 12,
                      marginBottom: 0,
                      lineHeight: 1.6,
                      color: "#262626",
                    }}
                  >
                    <strong>Flow Interpretation:</strong>{" "}
                    <strong>Project Management</strong> has ₹1.15 Cr own risk
                    exposure and cascades ₹1.15 Cr to Procurement & Inventory.{" "}
                    <strong>Procurement & Inventory</strong> (₹28.10L own)
                    receives ₹1.15 Cr from PM and cascades ₹28.10L to Work
                    Orders & Payments.{" "}
                    <strong>Work Order Execution & Payments</strong> (₹4.93L
                    own) receives ₹1.43 Cr total cascades and cascades ₹4.93L to
                    Budget. <strong>Budget Impact</strong> (₹87.50L own)
                    receives ₹1.48 Cr from upstream modules, creating ₹2.36 Cr
                    total exposure.
                  </p>
                </div>
              </motion.div>

              <Divider />

              {/* Module-wise Risk Breakdown */}
              {/* <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.7 }}
                style={{ marginBottom: 32 }}
              >
                <h5 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>
                  Module-wise Risk Breakdown
                </h5>
                {loading ? (
                  <ShimmerTableCard rows={8} />
                ) : (
                  <Table
                    columns={moduleRiskColumns}
                    dataSource={moduleRisks}
                    pagination={false}
                    bordered
                    size="small"
                    scroll={{ x: 1000 }}
                  />
                )}
              </motion.div>

              <Divider /> */}

              {/* Interlinked Impact Flow (Risk Cascade Table) */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.9 }}
                style={{ marginBottom: 32 }}
              >
                <h5 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>
                  Interlinked Impact Flow - Risk Cascade Analysis
                </h5>
                {loading ? (
                  <ShimmerTableCard rows={6} />
                ) : (
                  <Table
                    columns={cascadeColumns}
                    dataSource={riskCascades}
                    pagination={false}
                    bordered
                    size="small"
                    scroll={{ x: 1400 }}
                    // onRow={(record) => ({
                    //   onClick: () => {
                    //     setSelectedCascade(record);
                    //     setCascadeDrawerVisible(true);
                    //   },
                    //   style: { cursor: "pointer" },
                    // })}
                  />
                )}

                {/* <div style={{ marginTop: 16, padding: "12px 16px", background: "#fff7e6", borderRadius: 6, borderLeft: "3px solid #faad14" }}>
                  <p style={{ fontSize: 12, marginBottom: 6, lineHeight: 1.6, color: "#262626" }}>
                    <strong>💡 Business Logic:</strong> Every risk has a <strong>cascade multiplier</strong>:
                  </p>
                  <ul style={{ margin: 0, padding: 0, paddingLeft: 20, fontSize: 11, color: "#595959" }}>
                    <li>Procurement → Work Order: <strong>1.2×</strong></li>
                    <li>Work Order → Finance: <strong>1.1×</strong></li>
                    <li>Project Delay → Budget Overrun: <strong>1.3×</strong></li>
                  </ul>
                </div> */}
              </motion.div>

              <Divider />

              {/* Detailed Risk Breakdown by Module (Collapsible) */}
              {/* <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.1 }}
                style={{ marginBottom: 32 }}
              >
                <h5 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>
                  🔍 Detailed Risk Breakdown by Module
                </h5>
                {loading ? (
                  <ShimmerTableCard rows={4} />
                ) : (
                  <Collapse
                    bordered={false}
                    expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
                    style={{ background: "#fafafa" }}
                  >
                    <Panel
                      header={
                        <span style={{ fontWeight: 600, fontSize: 13 }}>
                          1️⃣ Project Management Risks (8 active, ₹7.5L impact)
                        </span>
                      }
                      key="1"
                    >
                      <div style={{ padding: "12px", background: "#fff", borderRadius: 6 }}>
                        <h6 style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>🔹 A. Delayed Tasks (5 tasks)</h6>
                        <p style={{ fontSize: 11, color: "#595959", marginBottom: 8 }}>
                          <strong>Detection:</strong> Actual End Date &gt; Planned End Date or Progress &lt; 90% of planned<br />
                          <strong>Impact:</strong> ₹20K-₹50K per delayed day per task → Cascades to Procurement PR bunching<br />
                          <strong>Mitigation:</strong> Reassign manpower from non-critical tasks + Enable auto-PR trigger
                        </p>
                        <h6 style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, marginTop: 12 }}>🔹 B. Resource Underutilization (3 instances)</h6>
                        <p style={{ fontSize: 11, color: "#595959", marginBottom: 0 }}>
                          <strong>Detection:</strong> Utilization &lt; 75%<br />
                          <strong>Impact:</strong> Cost waste + Idle labor cost<br />
                          <strong>Mitigation:</strong> Auto-shift planning based on utilization threshold
                        </p>
                      </div>
                    </Panel>
                    <Panel
                      header={
                        <span style={{ fontWeight: 600, fontSize: 13 }}>
                          2️⃣ Procurement & Inventory Risks (17 active, ₹16.5L impact)
                        </span>
                      }
                      key="2"
                    >
                      <div style={{ padding: "12px", background: "#fff", borderRadius: 6 }}>
                        <h6 style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>🔹 A. Delayed Purchase Requests (7 PRs)</h6>
                        <p style={{ fontSize: 11, color: "#595959", marginBottom: 8 }}>
                          <strong>Detection:</strong> Task Start Date &lt; 15 days away and related PR not approved<br />
                          <strong>Impact:</strong> Site stoppage + Delay cost = Days × Daily Site Overhead<br />
                          <strong>Mitigation:</strong> Auto-suggest PRs based on inventory shortage + 10-day alerts
                        </p>
                        <h6 style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, marginTop: 12 }}>🔹 B. Supplier Delivery Risk (4 delayed)</h6>
                        <p style={{ fontSize: 11, color: "#595959", marginBottom: 8 }}>
                          <strong>Detection:</strong> Expected Delivery &gt; Task Need-by Date<br />
                          <strong>Impact:</strong> Work halted + Daily loss = Task Cost × Delay Days<br />
                          <strong>Mitigation:</strong> Auto reassign to secondary supplier if delay &gt; 5 days
                        </p>
                        <h6 style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, marginTop: 12 }}>🔹 C. Stock-Out Risk (6 materials)</h6>
                        <p style={{ fontSize: 11, color: "#595959", marginBottom: 0 }}>
                          <strong>Detection:</strong> OnHand &lt; SafetyStock<br />
                          <strong>Impact:</strong> Emergency procurement = 15-20% cost increase<br />
                          <strong>Mitigation:</strong> Auto-suggest inter-site transfer for shortages &lt; 5 days lead
                        </p>
                      </div>
                    </Panel>
                    <Panel
                      header={
                        <span style={{ fontWeight: 600, fontSize: 13 }}>
                          3️⃣ Work Order Risks (6 active, ₹11.6L impact)
                        </span>
                      }
                      key="3"
                    >
                      <div style={{ padding: "12px", background: "#fff", borderRadius: 6 }}>
                        <h6 style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>🔹 A. Delayed Work Execution (4 WOs)</h6>
                        <p style={{ fontSize: 11, color: "#595959", marginBottom: 8 }}>
                          <strong>Detection:</strong> Actual progress &lt; Planned progress by 15%<br />
                          <strong>Impact:</strong> Avg delay 6 days → RA bills delayed → Payment blocked<br />
                          <strong>Mitigation:</strong> Auto alerts to contractor when variance &gt; 10%
                        </p>
                        <h6 style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, marginTop: 12 }}>🔹 B. Scope Deviation (2 WOs)</h6>
                        <p style={{ fontSize: 11, color: "#595959", marginBottom: 0 }}>
                          <strong>Detection:</strong> Actual Qty &gt; BOQ Qty × 1.05<br />
                          <strong>Impact:</strong> 5% excess inflates cost by 0.5%<br />
                          <strong>Mitigation:</strong> Amendment approval before processing excess work
                        </p>
                      </div>
                    </Panel>
                    <Panel
                      header={
                        <span style={{ fontWeight: 600, fontSize: 13 }}>
                          4️⃣ Financial & Budget Risks (8 active, ₹4.8L impact)
                        </span>
                      }
                      key="4"
                    >
                      <div style={{ padding: "12px", background: "#fff", borderRadius: 6 }}>
                        <h6 style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>🔹 A. Payment Delays (8 RA bills)</h6>
                        <p style={{ fontSize: 11, color: "#595959", marginBottom: 0 }}>
                          <strong>Detection:</strong> RA Bill approved but unpaid &gt; 15 days<br />
                          <strong>Impact:</strong> Contractor slows work → Cascading delays + Claim = Outstanding × 1.5%/month<br />
                          <strong>Mitigation:</strong> Payment SLA ≤ 15 days + Auto escalation for &gt;30-day cases
                        </p>
                      </div>
                    </Panel>
                  </Collapse>
                )}
              </motion.div>

              <Divider /> */}

              {/* Executive Insights */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.3 }}
                style={{ marginTop: 32 }}
              >
                <h5 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>
                  Executive-Level Insights
                </h5>

                <div
                  style={{
                    marginBottom: 20,
                    padding: "12px 16px",
                    background: "#fafafa",
                    borderRadius: 6,
                    borderLeft: "3px solid #ff4d4f",
                  }}
                >
                  <p
                    style={{
                      fontSize: 13,
                      marginBottom: 0,
                      lineHeight: 1.7,
                      color: "#595959",
                    }}
                  >
                    <strong style={{ color: "#cf1322" }}>
                      ⚠️ Critical Alert:
                    </strong>{" "}
                    Total Risk Index at{" "}
                    <strong>{overallRiskIndex.toFixed(1)}</strong> (Warning
                    level). Combined monetary exposure:{" "}
                    <strong>
                      {formatCurrency(totalMonetaryImpact, "INR")}
                    </strong>{" "}
                    (₹2.36 Cr). Immediate action required on{" "}
                    <strong>Budget Overrun (₹87.50L)</strong>,{" "}
                    <strong>Project Management Delays (₹1.15 Cr)</strong> and{" "}
                    <strong>Procurement Risks (₹28.10L)</strong>.
                  </p>
                </div>

                <div style={{ marginLeft: -8, marginRight: -8 }}>
                  <Row gutter={[16, 16]}>
                    <Col xs={24} lg={12}>
                      <div
                        style={{
                          padding: "16px",
                          background: "#fff",
                          borderRadius: 6,
                          border: "1px solid #e8e8e8",
                        }}
                      >
                        <h6
                          style={{
                            fontSize: 13,
                            fontWeight: 600,
                            marginBottom: 12,
                            color: "#262626",
                          }}
                        >
                          Critical Risk Areas
                        </h6>
                        <ul
                          style={{ margin: 0, padding: 0, listStyle: "none" }}
                        >
                          <li
                            style={{
                              fontSize: 12,
                              color: "#595959",
                              marginBottom: 8,
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                            }}
                          >
                            <WarningOutlined
                              style={{ color: "#ff4d4f", fontSize: 14 }}
                            />
                            <strong>Budget Overrun ₹87.50L</strong> → Critical
                            financial exposure
                          </li>
                          <li
                            style={{
                              fontSize: 12,
                              color: "#595959",
                              marginBottom: 8,
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                            }}
                          >
                            <WarningOutlined
                              style={{ color: "#ff4d4f", fontSize: 14 }}
                            />
                            <strong>Project Management Delays ₹1.15 Cr</strong>{" "}
                            → 42 delayed tasks
                          </li>
                          <li
                            style={{
                              fontSize: 12,
                              color: "#595959",
                              marginBottom: 0,
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                            }}
                          >
                            <WarningOutlined
                              style={{ color: "#faad14", fontSize: 14 }}
                            />
                            <strong>Procurement Risks ₹28.10L</strong> →
                            Material delivery delays
                          </li>
                        </ul>
                      </div>
                    </Col>
                    <Col xs={24} lg={12}>
                      <div
                        style={{
                          padding: "16px",
                          background: "#fff",
                          borderRadius: 6,
                          border: "1px solid #e8e8e8",
                        }}
                      >
                        <h6
                          style={{
                            fontSize: 13,
                            fontWeight: 600,
                            marginBottom: 12,
                            color: "#262626",
                          }}
                        >
                          Recommended Actions
                        </h6>
                        <ul
                          style={{ margin: 0, padding: 0, listStyle: "none" }}
                        >
                          <li
                            style={{
                              fontSize: 12,
                              color: "#595959",
                              marginBottom: 8,
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                            }}
                          >
                            <CheckCircleOutlined
                              style={{ color: "#52c41a", fontSize: 14 }}
                            />
                            Review and rebalance budget allocations (₹87.50L
                            overrun)
                          </li>
                          <li
                            style={{
                              fontSize: 12,
                              color: "#595959",
                              marginBottom: 8,
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                            }}
                          >
                            <CheckCircleOutlined
                              style={{ color: "#52c41a", fontSize: 14 }}
                            />
                            Reallocate resources to 42 delayed critical tasks
                          </li>
                          <li
                            style={{
                              fontSize: 12,
                              color: "#595959",
                              marginBottom: 0,
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                            }}
                          >
                            <CheckCircleOutlined
                              style={{ color: "#52c41a", fontSize: 14 }}
                            />
                            Fast-track procurement for 18 delayed deliveries
                            (₹28.10L impact)
                          </li>
                        </ul>
                      </div>
                    </Col>
                  </Row>
                </div>
              </motion.div>

              {/* <Divider /> */}

              {/* Geographic Risk Distribution Map */}
              {/* <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.5 }}
                style={{ marginTop: 32 }}
              >
                <h5 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>
                  <EnvironmentOutlined style={{ marginRight: 8, color: "#1890ff" }} />
                  Geographic Risk Distribution - IRA Projects
                </h5>

                <Row gutter={[24, 24]}>
                  <Col xs={24} lg={14}>
                    <div
                      style={{
                        padding: "16px",
                        background: "#fff",
                        borderRadius: 8,
                        border: "1px solid #e8e8e8",
                        height: "500px",
                        position: "relative",
                      }}
                    >
                      {loading ? (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            height: "100%",
                          }}
                        >
                          <Spin size="large" />
                        </div>
                      ) : (
                        <div
                          ref={mapRef}
                          style={{
                            height: "460px",
                            width: "100%",
                            borderRadius: "6px",
                            overflow: "hidden",
                          }}
                        />
                      )}
                    </div>
                  </Col>

                  <Col xs={24} lg={10}>
                    <div
                      style={{
                        padding: "16px",
                        background: "#fff",
                        borderRadius: 8,
                        border: "1px solid #e8e8e8",
                      }}
                    >
                      <h6
                        style={{
                          fontSize: 14,
                          fontWeight: 600,
                          marginBottom: 16,
                          color: "#262626",
                        }}
                      >
                        Project Risk Status
                      </h6>

                      <div
                        style={{
                          marginBottom: 20,
                          padding: "12px",
                          background: "#fafafa",
                          borderRadius: 6,
                        }}
                      >
                        <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>
                          Risk Level Legend
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <div
                              style={{
                                width: 16,
                                height: 16,
                                borderRadius: "50%",
                                background: "#ff4d4f",
                              }}
                            ></div>
                            <span style={{ fontSize: 11, color: "#595959" }}>
                              High Risk (2 projects)
                            </span>
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <div
                              style={{
                                width: 16,
                                height: 16,
                                borderRadius: "50%",
                                background: "#faad14",
                              }}
                            ></div>
                            <span style={{ fontSize: 11, color: "#595959" }}>
                              Medium Risk (2 projects)
                            </span>
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <div
                              style={{
                                width: 16,
                                height: 16,
                                borderRadius: "50%",
                                background: "#52c41a",
                              }}
                            ></div>
                            <span style={{ fontSize: 11, color: "#595959" }}>
                              Low Risk (1 project)
                            </span>
                          </div>
                        </div>
                      </div>

                      <div style={{ maxHeight: "360px", overflowY: "auto" }}>
                        {projectLocations.map((project, index) => (
                          <Tooltip
                            key={index}
                            title={`Click to navigate to ${project.name} details`}
                            placement="left"
                          >
                            <div
                              style={{
                                marginBottom: 12,
                                padding: "12px",
                                background: "#fafafa",
                                borderRadius: 6,
                                borderLeft: `3px solid ${project.riskColor}`,
                                cursor: "pointer",
                                transition: "all 0.3s",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = "#f0f5ff";
                                e.currentTarget.style.transform = "translateX(4px)";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = "#fafafa";
                                e.currentTarget.style.transform = "translateX(0)";
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "start",
                                  marginBottom: 6,
                                }}
                              >
                                <div style={{ flex: 1 }}>
                                  <div
                                    style={{
                                      fontSize: 13,
                                      fontWeight: 600,
                                      color: "#262626",
                                      marginBottom: 4,
                                    }}
                                  >
                                    {project.status} {project.name}
                                  </div>
                                  <div
                                    style={{
                                      fontSize: 11,
                                      color: "#8c8c8c",
                                      marginBottom: 6,
                                    }}
                                  >
                                    <EnvironmentOutlined
                                      style={{ fontSize: 10, marginRight: 4 }}
                                    />
                                    {project.location}
                                  </div>
                                </div>
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  fontSize: 11,
                                  color: "#595959",
                                }}
                              >
                                <span>
                                  Risk: {formatCurrency(project.riskValue, "INR")}
                                </span>
                                <span>Delayed: {project.delayedTasks} tasks</span>
                              </div>
                            </div>
                          </Tooltip>
                        ))}
                      </div>
                    </div>
                  </Col>
                </Row>

                <div
                  style={{
                    marginTop: 16,
                    padding: "12px 16px",
                    background: "#e6f7ff",
                    borderRadius: 6,
                    borderLeft: "3px solid #1890ff",
                  }}
                >
                  <p
                    style={{
                      fontSize: 12,
                      marginBottom: 0,
                      lineHeight: 1.6,
                      color: "#262626",
                    }}
                  >
                    <strong>💡 Geographic Insight:</strong> Project sites are
                    distributed across Hyderabad Metro Region (Kokapet, Kollur, and Adibatla areas). Marker size represents
                    risk magnitude, and color indicates risk severity. High-risk
                    projects (Grandeur Heights & Skyline Royale) require immediate
                    intervention to prevent cascading impacts across the portfolio.
                  </p>
                </div>
              </motion.div> */}
            </div>
          </div>
        </Card>
      </Content>

      {/* Risk Cascade Detail Drawer */}
      <Drawer
        title={
          <span>
            <ExclamationCircleOutlined
              style={{ marginRight: 8, color: "#ff4d4f" }}
            />
            Risk Cascade Detail
          </span>
        }
        placement="right"
        onClose={() => setCascadeDrawerVisible(false)}
        open={cascadeDrawerVisible}
        width="70%"
        style={{ maxWidth: 1000 }}
      >
        {selectedCascade && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            <Descriptions
              bordered
              column={1}
              size="small"
              style={{ marginBottom: 24 }}
            >
              <Descriptions.Item label="Risk Number">
                <strong>{selectedCascade.riskNumber}</strong>
              </Descriptions.Item>
              <Descriptions.Item label="Source Risk">
                <strong style={{ fontSize: 13 }}>
                  {selectedCascade.sourceRisk}
                </strong>
              </Descriptions.Item>
              <Descriptions.Item label="Origin Module">
                <Tag color="blue">{selectedCascade.originModule}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Cascade Impact Area">
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span>{selectedCascade.originModule}</span>
                  <ArrowRightOutlined style={{ color: "#ff4d4f" }} />
                  <strong>{selectedCascade.cascadeImpactArea}</strong>
                </div>
              </Descriptions.Item>
              <Descriptions.Item label="Detection Logic">
                <div
                  style={{
                    padding: "8px 12px",
                    background: "#f0f5ff",
                    borderRadius: 4,
                    fontSize: 12,
                  }}
                >
                  {selectedCascade.detectionLogic}
                </div>
              </Descriptions.Item>
              <Descriptions.Item label="Type of Impact">
                <Tag color="orange" style={{ fontSize: 12 }}>
                  {selectedCascade.typeOfImpact}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Monetary Impact">
                <strong style={{ color: "#ff4d4f", fontSize: 16 }}>
                  {formatCurrency(selectedCascade.monetaryImpact, "INR")}
                </strong>
              </Descriptions.Item>
              <Descriptions.Item label="Example Calculation">
                <div
                  style={{
                    padding: "8px 12px",
                    background: "#fff7e6",
                    borderRadius: 4,
                    fontSize: 12,
                    fontFamily: "monospace",
                  }}
                >
                  {selectedCascade.exampleImpact}
                </div>
              </Descriptions.Item>
              <Descriptions.Item label="Responsible Person">
                {selectedCascade.responsiblePerson}
              </Descriptions.Item>
              <Descriptions.Item label="Severity">
                <Tag
                  color={
                    selectedCascade.severity === "High"
                      ? "red"
                      : selectedCascade.severity === "Medium"
                      ? "orange"
                      : "green"
                  }
                >
                  {selectedCascade.severity}
                </Tag>
              </Descriptions.Item>
            </Descriptions>

            <div
              style={{
                padding: "12px 16px",
                background: "#e6f7ff",
                borderRadius: 6,
                borderLeft: "3px solid #1890ff",
              }}
            >
              <h6
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  marginBottom: 8,
                  color: "#262626",
                }}
              >
                💡 Business Logic
              </h6>
              <p style={{ fontSize: 12, marginBottom: 6, color: "#595959" }}>
                <strong>Delay Cost Formula:</strong> Daily Cost of Resource/Task
                × Delay Days
              </p>
              <p style={{ fontSize: 12, marginBottom: 0, color: "#595959" }}>
                <strong>Cascade Multiplier:</strong> Each downstream module
                amplifies the impact by 1.1× to 1.3×
              </p>
            </div>
          </motion.div>
        )}
      </Drawer>
    </>
  );
};

export default IntegratedRiskControlTower;
